import {Dispatch, FC, SetStateAction, useCallback, useEffect, useState} from 'react'
import clsx from 'clsx'
import {Link, useLocation} from 'react-router-dom'
import axios from 'axios'
import {getUserByToken} from '../core/_requests'
import {
  API_URL,
  GetBrowserData,
  fixNumbers,
  isValidNationalCode,
} from '../../../../_metronic/helpers'
import {CustomInputs} from '../../../../_metronic/helpers/components/CustomInputs'
import {useAuth} from '../core/Auth'
import {AuthModel} from '../core/_models'
import {isForgotPasswordLogin, rememberLoginRoute} from '../core/credentialNotice'
import {accountDisabledMessage} from '../core/accountDisabled'
import './Login.css'
import {CaptchaStore, LoginCaptcha, createCaptchaStore} from './LoginCaptcha'
import {TaxFolderSsoButton} from './TaxFolderSsoButton'

type props = {
  title?: string
  simpleMode?: boolean
  successFn?: () => void
  setMobile?: Dispatch<SetStateAction<string>>
  /** A mobile to offer (a partner's sign-up link names the taxpayer's); still editable. */
  defaultMobile?: string
}

const OTP_LENGTH = 5
const OTP_LIFETIME_SECONDS = 5 * 60
const RESEND_DELAY_SECONDS = 2 * 60
const MAX_OTP_ATTEMPTS = 3

const toClock = (totalSeconds: number) => {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
}

const LoginMobile: FC<props> = ({
  title = '',
  successFn,
  simpleMode = false,
  setMobile: setParentMobile,
  defaultMobile,
}) => {
  const [loading, setLoading] = useState(false)
  const [codeSent, setCodeSent] = useState(false)
  const [codeExpired, setCodeExpired] = useState(false)
  const [otpFocused, setOtpFocused] = useState(false)
  const [status, setStatus] = useState<string>()
  const [code, setCode] = useState('')
  const [mobile, setMobile] = useState(() =>
    defaultMobile ? fixNumbers(defaultMobile).replace(/\D/g, '').slice(0, 11) : ''
  )
  const [failedAttempts, setFailedAttempts] = useState(0)
  const [otpSecondsLeft, setOtpSecondsLeft] = useState(OTP_LIFETIME_SECONDS)
  const [resendSecondsLeft, setResendSecondsLeft] = useState(RESEND_DELAY_SECONDS)
  const [getNationalCode, setGetNationalCode] = useState(false)
  const [nationalCode, setNationalCode] = useState('')
  const [nationalCodeError, setNationalCodeError] = useState('')
  const [birthDate, setBirthDate] = useState('')
  const [captcha, setCaptcha] = useState<CaptchaStore>(() => createCaptchaStore())
  const {saveAuth, setCurrentUser} = useAuth()
  const location = useLocation()

  const refreshCaptcha = useCallback(() => setCaptcha(createCaptchaStore()), [])

  const expireCurrentCode = useCallback(
    (message: string) => {
      setCodeSent(false)
      setCodeExpired(true)
      setCode('')
      setFailedAttempts(MAX_OTP_ATTEMPTS)
      setGetNationalCode(false)
      refreshCaptcha()
      setStatus(message)
    },
    [refreshCaptcha]
  )

  useEffect(() => {
    let error = ''
    if (nationalCode.length === 10 && !isValidNationalCode(nationalCode)) {
      error = 'کد ملی واردشده معتبر نیست.'
    } else if (nationalCode.length > 10) {
      setNationalCode(nationalCode.substring(0, 10))
    }
    setNationalCodeError(error)
  }, [nationalCode])

  useEffect(() => {
    if (!codeSent || getNationalCode) return
    if (otpSecondsLeft <= 0) {
      expireCurrentCode('زمان استفاده از کد به پایان رسید. برای ادامه، کد جدید دریافت کنید.')
      return
    }

    const timer = window.setTimeout(() => setOtpSecondsLeft((seconds) => seconds - 1), 1000)
    return () => window.clearTimeout(timer)
  }, [codeSent, expireCurrentCode, getNationalCode, otpSecondsLeft])

  useEffect(() => {
    if (!codeSent || resendSecondsLeft <= 0) return
    const timer = window.setTimeout(() => setResendSecondsLeft((seconds) => seconds - 1), 1000)
    return () => window.clearTimeout(timer)
  }, [codeSent, resendSecondsLeft])

  useEffect(() => {
    if (codeSent && !getNationalCode && code.length === OTP_LENGTH && !loading) {
      submit()
    }
    // submit is intentionally triggered only when the OTP value changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code])

  const handleMobileChange = (value: string) => {
    const normalizedMobile = fixNumbers(value).replace(/\D/g, '').slice(0, 11)
    setMobile(normalizedMobile)
    if (codeExpired) setCodeExpired(false)
  }

  const requestCodeDisabled =
    loading || mobile.length !== 11 || !mobile.startsWith('09') || captcha.captcha.length !== 4

  const verifyDisabled =
    loading ||
    failedAttempts >= MAX_OTP_ATTEMPTS ||
    (!getNationalCode && code.length !== OTP_LENGTH) ||
    (getNationalCode && (!isValidNationalCode(nationalCode) || birthDate === ''))

  async function submit() {
    setStatus(undefined)

    if (!codeSent) {
      if (requestCodeDisabled) return

      setLoading(true)
      if (setParentMobile) setParentMobile(mobile)

      try {
        const browser = await GetBrowserData()
        await axios.post(`${API_URL}/Auth/Mobile`, {mobile, captcha, browser})
        setCodeSent(true)
        setCodeExpired(false)
        setCode('')
        setFailedAttempts(0)
        setOtpSecondsLeft(OTP_LIFETIME_SECONDS)
        setResendSecondsLeft(RESEND_DELAY_SECONDS)
        window.setTimeout(() => document.getElementById('otpCode')?.focus(), 100)
      } catch (error: any) {
        const responseStatus = error?.response?.status
        refreshCaptcha()
        if (responseStatus === 400) {
          setStatus('کد قبلی به‌تازگی ارسال شده است. لطفاً پیامک‌های خود را بررسی کنید.')
        } else if (responseStatus === 403) {
          setStatus('کد امنیتی صحیح نیست. کد جدید نمایش‌داده‌شده را وارد کنید.')
        } else if (responseStatus === 502) {
          setStatus('شماره موبایل واردشده معتبر نیست یا اجازه ورود ندارد.')
        } else {
          setStatus('ارسال کد با مشکل مواجه شد. چند لحظه دیگر دوباره تلاش کنید.')
        }
      } finally {
        setLoading(false)
      }
      return
    }

    if (verifyDisabled) return
    setLoading(true)

    try {
      const browser = await GetBrowserData()
      const response = await axios.post(`${API_URL}/Auth/MobileCode`, {
        mobile,
        code: fixNumbers(code),
        nationalCode,
        birthDate,
        browser,
        shahkar: getNationalCode,
      })
      const auth = response.data as AuthModel

      if (auth.id === -1) {
        setGetNationalCode(true)
        return
      }

      saveAuth(auth)
      const {data: user} = await getUserByToken(auth.tk || '')
      rememberLoginRoute(isForgotPasswordLogin(location.search))
      setCurrentUser(user)
      if (successFn) successFn()
    } catch (error: any) {
      const responseStatus = error?.response?.status
      saveAuth(undefined)
      const disabled = accountDisabledMessage(error)

      if (disabled) {
        // The code was right; the account is switched off. Another code would not help.
        expireCurrentCode(disabled)
      } else if (responseStatus === 417 || responseStatus === 502) {
        expireCurrentCode('این کد دیگر قابل استفاده نیست. برای ادامه، کد جدید دریافت کنید.')
      } else if (responseStatus === 401 || responseStatus === 403) {
        const nextFailedAttempts = failedAttempts + 1
        setFailedAttempts(nextFailedAttempts)
        setCode('')

        if (nextFailedAttempts >= MAX_OTP_ATTEMPTS) {
          expireCurrentCode('سه بار کد اشتباه وارد شد و کد منقضی شد. لطفاً کد جدید دریافت کنید.')
        } else {
          const remainingAttempts = MAX_OTP_ATTEMPTS - nextFailedAttempts
          setStatus(`کد واردشده صحیح نیست. ${remainingAttempts} فرصت دیگر باقی مانده است.`)
          window.setTimeout(() => document.getElementById('otpCode')?.focus(), 100)
        }
      } else if (responseStatus === 400) {
        setStatus('کد ملی یا تاریخ تولد واردشده صحیح نیست.')
      } else if (responseStatus === 503) {
        setStatus('سرویس استعلام هویت در دسترس نیست. کمی بعد دوباره تلاش کنید.')
      } else {
        setStatus('ورود انجام نشد. اتصال اینترنت را بررسی و دوباره تلاش کنید.')
      }
    } finally {
      setLoading(false)
    }
  }

  const prepareNewCode = () => {
    setCodeSent(false)
    setCodeExpired(false)
    setCode('')
    setFailedAttempts(0)
    setGetNationalCode(false)
    setStatus('برای دریافت کد جدید، کد امنیتی را وارد کنید.')
    refreshCaptcha()
  }

  const changeMobile = () => {
    setCodeSent(false)
    setCodeExpired(false)
    setStatus(undefined)
    setMobile('')
    setCode('')
    setFailedAttempts(0)
    setGetNationalCode(false)
    refreshCaptcha()
  }

  return (
    <div className={clsx('mobile-login-flow', simpleMode && 'mobile-login-flow--simple')}>
      {!simpleMode && (
        <>
          <div className='auth-flow-head'>
            <h1 className='auth-title'>{title || 'ورود با شماره موبایل'}</h1>
            <p>کد یک‌بارمصرف به شماره موبایل شما پیامک می‌شود.</p>
          </div>
          <div className='auth-method-switch' aria-label='روش ورود'>
            <span className='auth-method-switch__item active'>شماره موبایل</span>
            <Link to='/auth/login' className='auth-method-switch__item'>
              نام کاربری
            </Link>
          </div>
          <TaxFolderSsoButton />
        </>
      )}

      {status && (
        <div
          className={clsx(
            'auth-message',
            codeExpired ? 'auth-message--warning' : 'auth-message--danger'
          )}
          role='alert'
        >
          <i
            className={clsx('las', codeExpired ? 'la-exclamation-triangle' : 'la-info-circle')}
            aria-hidden='true'
          />
          <span>{status}</span>
        </div>
      )}

      <div className='auth-field'>
        <label htmlFor='mobileNumber'>شماره موبایل</label>
        <div className='auth-input-wrap'>
          <i className='las la-mobile-alt' aria-hidden='true' />
          <input
            id='mobileNumber'
            className='form-control'
            type='tel'
            inputMode='numeric'
            autoComplete='tel'
            dir='ltr'
            value={mobile}
            disabled={loading || codeSent}
            placeholder='0912 123 4567'
            aria-describedby='mobileHint'
            onChange={(event) => handleMobileChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                submit()
              }
            }}
          />
        </div>
        {!codeSent && <small id='mobileHint'>کد ورود به همین شماره پیامک می‌شود.</small>}
      </div>

      {!codeSent && (
        <LoginCaptcha value={captcha} onChange={setCaptcha} disabled={loading} onEnter={submit} />
      )}

      {!getNationalCode && codeSent && (
        <div className='otp-stage'>
          <div className='otp-stage__heading'>
            <div>
              <strong>کد تأیید را وارد کنید</strong>
              <span>کد {OTP_LENGTH} رقمی ارسال‌شده به شماره بالا</span>
            </div>
            <span
              className='otp-stage__timer'
              aria-label={`زمان باقی‌مانده ${toClock(otpSecondsLeft)}`}
              dir='ltr'
            >
              <i className='las la-clock' aria-hidden='true' /> {toClock(otpSecondsLeft)}
            </span>
          </div>

          <div className={clsx('otp-inputs', otpFocused && 'is-focused')} dir='ltr'>
            <input
              id='otpCode'
              className='otp-native-input'
              type='text'
              inputMode='numeric'
              pattern='[0-9]*'
              enterKeyHint='done'
              autoComplete='one-time-code'
              maxLength={OTP_LENGTH}
              value={code}
              disabled={loading}
              aria-label={`کد تأیید ${OTP_LENGTH} رقمی`}
              onFocus={() => setOtpFocused(true)}
              onBlur={() => setOtpFocused(false)}
              onChange={(event) =>
                setCode(fixNumbers(event.target.value).replace(/\D/g, '').slice(0, OTP_LENGTH))
              }
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  submit()
                }
              }}
            />
            <div className='otp-boxes' aria-hidden='true'>
              {Array.from({length: OTP_LENGTH}).map((_, index) => (
                <span
                  key={index}
                  className={clsx(
                    'otp-box',
                    code[index] && 'is-filled',
                    otpFocused && index === Math.min(code.length, OTP_LENGTH - 1) && 'is-active'
                  )}
                >
                  {code[index] || ''}
                </span>
              ))}
            </div>
          </div>

          <div className='otp-stage__meta'>
            <span>
              تلاش باقی‌مانده: <b>{MAX_OTP_ATTEMPTS - failedAttempts}</b>
            </span>
            <span>اعتبار کد: ۵ دقیقه</span>
          </div>

          <div className='otp-stage__actions'>
            <button
              type='button'
              className='auth-link-button'
              onClick={changeMobile}
              disabled={loading}
            >
              <i className='las la-pen' aria-hidden='true' /> تغییر شماره
            </button>
            <button
              type='button'
              className='auth-link-button auth-link-button--accent'
              onClick={prepareNewCode}
              disabled={loading || resendSecondsLeft > 0}
            >
              <i className='las la-redo-alt' aria-hidden='true' />
              {resendSecondsLeft > 0
                ? `ارسال مجدد تا ${toClock(resendSecondsLeft)}`
                : 'ارسال مجدد کد'}
            </button>
          </div>
        </div>
      )}

      {getNationalCode && (
        <div className='identity-stage'>
          <div className='auth-inline-notice'>
            برای تکمیل ورود، اطلاعات هویتی صاحب شماره را وارد کنید.
          </div>
          <CustomInputs
            type='text'
            label='کد ملی'
            stateValue={{value: nationalCode, setValue: setNationalCode}}
            error={nationalCodeError}
            LTR
            style={{letterSpacing: '5px'}}
            isDisabled={loading}
          />
          <CustomInputs
            type='datepicker'
            label='تاریخ تولد'
            stateValue={{value: birthDate, setValue: setBirthDate}}
            isDisabled={loading}
          />
        </div>
      )}

      <button
        type='button'
        id='kt_sign_in_submit'
        className='btn btn-primary auth-submit'
        onClick={submit}
        disabled={codeSent ? verifyDisabled : requestCodeDisabled}
      >
        {loading ? (
          <span>
            <span className='spinner-border spinner-border-sm align-middle ms-2' /> لطفاً صبر
            کنید...
          </span>
        ) : (
          <span>
            {getNationalCode
              ? 'تأیید اطلاعات و ورود'
              : codeSent
              ? 'تأیید و ورود'
              : codeExpired
              ? 'دریافت کد جدید'
              : 'دریافت کد ورود'}
            <i className='las la-arrow-left' aria-hidden='true' />
          </span>
        )}
      </button>

      {!simpleMode && (
        <p className='auth-security-note'>
          <i className='las la-shield-alt' aria-hidden='true' /> کد ورود محرمانه است؛ آن را در
          اختیار دیگران قرار ندهید.
        </p>
      )}
    </div>
  )
}

export {LoginMobile}
